import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { faArrowsRotate, faCircleExclamation, faExclamationTriangle, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';
import { RowOrderDirection } from 'src/app/shared/components/row-order-indicator/row-order-indicator.component';
import { CommonNoteViewModel, ComponentsInPipeClassViewModel, ValveValidationResult, ValveViewModel } from 'src/app/shared/models/autogenerated-piping';
import { CommentViewModel } from 'src/app/shared/models/comment-api.models';
import { NpsValidationService } from 'src/app/shared/services/nps-validation.service';

interface CommonNoteWithCounter {
  note: CommonNoteViewModel,
  counter: number
}

/**
 * Per-row diff result computed when compare mode is active.
 * Matches the current valve to its counterpart in the previous revision by tag.
 */
interface ValveRowDiff {
  valve: ValveViewModel;
  /** The matching valve from the previous revision, or null when the tag is new. */
  previousValve: ValveViewModel | null;
  /** Direction the row moved relative to its previous position in the list. */
  orderDirection: RowOrderDirection;
  /** True when this tag does not appear in the previous revision. */
  isNew: boolean;
  /** True for ghost rows: valve present in the previous revision but absent from the current list. */
  isDeleted: boolean;
}

/**
 * A single navigable target in the valve tag dropdown: a specific catalog version of the valve
 * plus a human-readable status explaining why it's relevant (published/draft/current).
 */
interface ValveVersionOption {
  id: string;
  version: ValveViewModel;
  statusLabel: string;
}

@Component({
    selector: 'app-valve-tab',
    styleUrls: ['valve-tab.component.scss'],
    templateUrl: 'valve-tab.component.html',
    standalone: false
})
export class ValveTabComponent implements OnInit, OnChanges {
  @Input() pipeClassViewModelValves: ValveViewModel[];
  @Input() pipeClassViewModelComponents: ComponentsInPipeClassViewModel[];
  @Input() canEdit$: Observable<boolean>;
  /** True for any user who arrived from the revisions dashboard (authors + reviewers).
   *  Used to show the comments section even for reviewers who cannot edit fields. */
  @Input() isInRevisionWorkflow: boolean = false;
  @Input() currentRevisionName: string; // Current revision name for comparison
  @Input() isRevisionInProgress: boolean = false; // Whether the revision is being edited (not published)
  @Input() pipeClassId: string; // For comments section
  @Input() revisionId: string; // For comments section
  @Input() comments: CommentViewModel[] = []; // Comments for auto-save
  @Input() commentsSaving$: Observable<boolean>; // Auto-save loading state
  @Input() commentsLoading$: Observable<boolean>; // True while comments are being fetched
  @Input() commonNotesVersion: number = 0;
  @Output() addValvesEvent = new EventEmitter<void>();
  @Output() editValveNotesEvent = new EventEmitter<ValveViewModel>();
  @Output() removeValveEvent = new EventEmitter<ValveViewModel>();
  @Output() editValveGroupNotesEvent = new EventEmitter<void>();
  @Output() reorderValvesEvent = new EventEmitter<ValveViewModel[]>();
  @Output() updateValveNpsEvent = new EventEmitter<{valve: ValveViewModel, newNps: string, index: number, componentsInPipeClassId: string}>();
  @Output() updateValveRatingsEvent = new EventEmitter<{valve: ValveViewModel, newRatings: string, index: number, componentsInPipeClassId: string}>();
  @Output() updateValveEndsEvent = new EventEmitter<{valve: ValveViewModel, newEnds: string, index: number, componentsInPipeClassId: string}>();
  @Output() updateValveDescriptionEvent = new EventEmitter<{valve: ValveViewModel, newDescription: string, index: number, componentsInPipeClassId: string}>();
  @Output() commentsChanged = new EventEmitter<CommentViewModel[]>(); // Comments change event
  @Output() saveCommentsRequested = new EventEmitter<void>(); // Auto-save trigger

  @Input() isCompareMode: boolean = false;
  /** Valves from the previous revision, used to compute row-level diffs in compare mode. */
  @Input() previousValves: ValveViewModel[] = [];
  /** Catalog validation results keyed by ValveViewModel.id, passed from piping-tabs-parent. */
  @Input() valveValidationResults: Map<string, ValveValidationResult> = new Map();
  /** Emitted when the user requests replacing a valve with its latest catalog version. */
  @Output() replaceValveEvent = new EventEmitter<{ valve: ValveViewModel; latestVersion: ValveViewModel }>();

  valvesClicked = true;
  valvesPresent = false;
  mutualNotes: CommonNoteViewModel[] = [];

  /**
   * Precomputed row diffs for the current valve list.
   * Always populated (even outside compare mode) so the template can use a single *ngFor.
   */
  valveRowDiffs: ValveRowDiff[] = [];

  /** Valves present in the previous revision but absent from the current one.
   *  Rendered as deleted (red) rows at the end of the table in compare mode. */
  deletedValveRows: ValveViewModel[] = [];

  /** Mutual (group) notes from the previous revision, computed in compare mode. */
  previousMutualNotes: CommonNoteViewModel[] = [];

  /** Note codes shared across all current valves — cached for use in the group-notes diff. */
  get currentMutualNoteCodes(): string[] {
    return this.mutualNotes.map(n => n.codeId);
  }

  /** Note codes shared across all previous valves — cached for use in the group-notes diff. */
  get previousMutualNoteCodes(): string[] {
    return this.previousMutualNotes.map(n => n.codeId);
  }

  // NPS editing properties
  editingValveIndex: number | null = null;
  editingNpsValue: string = '';
  npsValidationError: string = '';
  faTrashCan = faTrashCan;
  faArrowsRotate = faArrowsRotate;
  faExclamationTriangle = faExclamationTriangle;
  /** Shown as a passive orange badge when the valve is outdated but the user cannot edit. */
  faCircleExclamation = faCircleExclamation;

  editingRatingIndex: number | null = null;
  editingRatingValue: string = '';

  editingEndsIndex: number | null = null;
  editingEndsValue: string = '';

  editingDescriptionIndex: number | null = null;
  editingDescriptionValue: string = '';

  private readonly destroyRef = inject(DestroyRef);
  /** Synchronous snapshot of canEdit$. Updated on every emission so the upgrade click handler can guard without the async pipe. */
  isEditable: boolean = false;

  constructor(
    private router: Router,
    private npsValidationService: NpsValidationService
  ) {}

  ngOnInit() {
    // Track canEdit$ as a plain boolean so click handlers can guard synchronously,
    // and so aria-disabled can be set without a second async pipe evaluation.
    this.canEdit$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => { this.isEditable = v; });
    this.valvesPresent = this.setValvesPresent();
    if (this.pipeClassViewModelValves && this.pipeClassViewModelValves.length > 0) {
      this.mutualNotes = this.setMutualNotes(this.pipeClassViewModelValves);
    }
    this.computeValveDiff();
  }

  ngOnChanges(): void {
    // Reset editing state when the valve list changes to prevent stale index references
    this.cancelAllEditing();

    this.valvesPresent = this.setValvesPresent();

    if (this.pipeClassViewModelValves && this.pipeClassViewModelValves.length > 0) {
      this.sortValves();
      this.mutualNotes = this.setMutualNotes(this.pipeClassViewModelValves);
      this.sortCommonNotes();
    }
    this.computeValveDiff();
  }

  setValvesPresent(): boolean {
    return this.pipeClassViewModelValves && this.pipeClassViewModelValves.length > 0;
  }

  toggleValvesClicked() {
    this.valvesClicked = !this.valvesClicked;
  }

  onAddValves(): void {
    this.addValvesEvent.emit();
  }

  onEditGroupValveNotes(): void { this.editValveGroupNotesEvent.emit(); }

  onEditValveNotes(valve: ValveViewModel): void {
    this.editValveNotesEvent.emit(valve);
  }

  onRemoveValve(valve: ValveViewModel): void {
    this.removeValveEvent.emit(valve);
  }

  // ── Catalog validation helpers ───────────────────────────────────────────

  /** Returns the validation result for a valve, or undefined if not yet available. */
  getValveValidationResult(valve: ValveViewModel): ValveValidationResult | undefined {
    if (!valve?.id) { return undefined; }
    return this.valveValidationResults.get(valve.id);
  }

  /** True when the valve is outdated and a replacement is available. */
  valveHasUpgrade(valve: ValveViewModel): boolean {
    const result = this.getValveValidationResult(valve);
    return !!result && result.isCurrent === false && !!result.latestVersion;
  }

  /** True when the valve is outdated and no replacement can be determined (deprecated). */
  valveIsDeprecated(valve: ValveViewModel): boolean {
    const result = this.getValveValidationResult(valve);
    return !!result && result.isCurrent === false && !result.latestVersion;
  }

  /** Tooltip for the indicator button.
   * Appends "Click to update." when the user can edit and an upgrade is available. */
  getValveValidationTooltip(valve: ValveViewModel, canEdit: boolean | null = false): string {
    const result = this.getValveValidationResult(valve);
    if (!result || result.isCurrent) { return ''; }
    if (result.latestVersion) {
      const base: string = result.message || 'A newer version of this valve is available.';
      return canEdit ? `${base}\nClick to update.` : base;
    }
    return result.message || 'This valve is deactivated and no replacement is available.';
  }

  /** Called when the user clicks the upgrade indicator button. */
  onReplaceValve(valve: ValveViewModel): void {
    // isEditable guards against keyboard activation while aria-disabled is true.
    if (!this.isEditable) { return; }
    const result = this.getValveValidationResult(valve);
    if (!result?.latestVersion) { return; }
    this.replaceValveEvent.emit({ valve, latestVersion: result.latestVersion });
  }

  sortValves() {
    if (this.pipeClassViewModelValves && this.pipeClassViewModelValves.length > 0) {
      this.pipeClassViewModelValves.sort((a, b) => a.displayOrder - b.displayOrder);
    }
  }

  sortCommonNotes() {
    if (this.mutualNotes && this.mutualNotes.length > 0) {
      this.mutualNotes.sort((a, b) => Number(a.codeId) - Number(b.codeId));
    }
  }

  openValveSummaryPage(valve: ValveViewModel): void {
    // Superseded valves link to the latest catalog version, not the (possibly deprecated) id on the row.
    // Never redirects to a draft here -- latestVersion may be unpublished, use the dropdown for that.
    const result = this.getValveValidationResult(valve);
    const targetValveId: string | undefined = (result?.isCurrent === false && result.latestPublishedVersion?.id)
      ? result.latestPublishedVersion.id
      : valve.id;
    this.router.navigate(['/valvesummary', targetValveId]);
  }

  /**
   * Builds the deduplicated, priority-ordered list of navigable versions for a valve tag:
   * latest published catalog version, latest draft (only if it differs from the published
   * one), and the version currently assigned to this pipe class. Ids that coincide with an
   * earlier entry are merged rather than duplicated -- e.g. when the row is already on the
   * latest published version, that single entry is relabeled "Currently in this Pipe Class"
   * instead of appearing twice.
   * @param valve The valve as currently assigned to this pipe class row.
   * @returns Up to 3 distinct, navigable valve versions, each with a status label.
   */
  getValveVersionOptions(valve: ValveViewModel): ValveVersionOption[] {
    const result: ValveValidationResult | undefined = this.getValveValidationResult(valve);
    const options: ValveVersionOption[] = [];

    const latestPublished: ValveViewModel | undefined = result?.latestPublishedVersion;
    if (latestPublished?.id) {
      options.push({ id: latestPublished.id, version: latestPublished, statusLabel: 'Latest Published' });
    }

    // Only surface the draft as a separate option when it differs from the published version --
    // otherwise "latest" and "published" resolve to the same target and showing both is redundant.
    const draft: ValveViewModel | undefined = result?.latestVersion;
    if (draft?.id && draft.id !== latestPublished?.id) {
      options.push({ id: draft.id, version: draft, statusLabel: 'Draft in Progress' });
    }

    const currentId: string | undefined = valve.id;
    const matchingOption: ValveVersionOption | undefined = options.find(option => option.id === currentId);
    if (matchingOption) {
      matchingOption.statusLabel = 'Currently in this Pipe Class';
    } else if (currentId) {
      options.push({ id: currentId, version: valve, statusLabel: 'Currently in this Pipe Class' });
    }

    return options;
  }

  /** Displays a version's revision name for the dropdown, or a placeholder if unavailable. */
  getRevisionDisplayName(version: ValveViewModel): string {
    return version?.fromRevision?.name || '-';
  }

  /** Navigates to the valve summary page for the selected dropdown option. */
  navigateToValveVersion(option: ValveVersionOption): void {
    this.router.navigate(['/valvesummary', option.id]);
  }

  replaceUnderscoresWithSpaces(inputStr: string | undefined): string {
    if (!inputStr) { return ''; }
    const strArr: string[] = inputStr.split('_');

    if (strArr.length > 1) {
      strArr.shift();
      return strArr.join(" ");
    } else {
      return inputStr;
    }
  }

  extractSubstringBeforeUnderscore(str: string | undefined): string {
    if (!str) { return ''; }
    return str.split('_')[0];
  }

  getJoinedCommonNoteCodes(componentInPipeClass: ComponentsInPipeClassViewModel): string {
    if (componentInPipeClass.commonNotes && componentInPipeClass.commonNotes.length > 0) {
      return componentInPipeClass.commonNotes.map(commonNote => commonNote.codeId).join(", ");
    }
    return "";
  }

  setMutualNotes(valves: ValveViewModel[]): CommonNoteViewModel[] {
    const counterMap = new Map<string, CommonNoteWithCounter>();
    valves.forEach(valve => (valve.commonNotes || []).forEach(note => {
                  const noteAndCounter = counterMap.get(note.codeId);
                  let counter = noteAndCounter?.counter ?? 0
                  counterMap.set(note.codeId, {note: note, counter: ++counter});
                }));

    let returnNotes: CommonNoteViewModel[] = [];
    for (let [key, value] of counterMap) {
      if (value.counter == valves.length) {
        returnNotes.push(value.note);
      }
    };

    return returnNotes;
  }

  excludeMutualNotes(notes: Array<CommonNoteViewModel>): Array<CommonNoteViewModel> {
    return notes.filter(note => this.mutualNotes.filter(nt => nt.codeId === note.codeId).length == 0)
    .sort((a, b) => Number(a.codeId) - Number(b.codeId));
  }

  // ── Compare mode helpers ──────────────────────────────────────────────────

  /**
   * Builds `valveRowDiffs` and `deletedValveRows` from the current and previous valve lists.
   * Called in both `ngOnInit` and `ngOnChanges` so the diff stays current as inputs change.
   *
   * **Matching strategy:** rows are grouped by component type (same sub-group key as the UI),
   * then matched by `valve.tag` within each type. This mirrors the component tab approach:
   * a Gate Valve is never compared against a Ball Valve even if they share the same display index.
   * Within each type, tags not found in the previous revision are new (green); previous tags
   * not found in the current revision become deleted (red ghost rows).
   */
  private computeValveDiff(): void {
    // Sort by displayOrder so direction indicators reflect the order the user sees.
    const currentList: ValveViewModel[] = [...(this.pipeClassViewModelValves || [])]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const sortedPreviousValves: ValveViewModel[] = [...(this.previousValves || [])]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    // In compare mode, pre-compute the previous mutual notes so we can exclude them per-row.
    // `setMutualNotes` is null-safe on `commonNotes` since previous revision data may omit the field.
    this.previousMutualNotes = (this.isCompareMode && sortedPreviousValves.length > 0)
      ? this.setMutualNotes(sortedPreviousValves)
      : [];

    if (!this.isCompareMode || sortedPreviousValves.length === 0) {
      // Non-compare mode: wrap each valve with a neutral diff so the template can use valveRowDiffs uniformly
      this.valveRowDiffs = currentList.map(valve => ({
        valve,
        previousValve: null,
        orderDirection: 'unchanged' as RowOrderDirection,
        isNew: false,
        isDeleted: false
      }));
      this.deletedValveRows = [];
      return;
    }

    // Resolve the display type string the same way the template does.
    const getTypeKey = (v: ValveViewModel): string =>
      v.componentTypeString || this.replaceUnderscoresWithSpaces(v.componentType?.name) || '';

    // Group previous valves by component type so each type is matched independently.
    const prevByType = new Map<string, ValveViewModel[]>();
    for (const valve of sortedPreviousValves) {
      const key: string = getTypeKey(valve);
      if (!prevByType.has(key)) { prevByType.set(key, []); }
      prevByType.get(key).push(valve);
    }

    // Track which previous valves were matched so deleted ones can be identified.
    const matchedPreviousValves = new Set<ValveViewModel>();

    this.valveRowDiffs = currentList.map((valve: ValveViewModel) => {
      const typeKey: string = getTypeKey(valve);
      const prevSameType: ValveViewModel[] = prevByType.get(typeKey) ?? [];
      const previousValve: ValveViewModel | null = prevSameType.find(pv => pv.tag === valve.tag) ?? null;

      if (previousValve !== null) { matchedPreviousValves.add(previousValve); }

      // Direction indicator: compare position within the same-type sub-list.
      const currentTypeIndex: number = currentList.filter(v => getTypeKey(v) === typeKey).indexOf(valve);
      const previousTypeIndex: number = prevSameType.findIndex(pv => pv.tag === valve.tag);
      let orderDirection: RowOrderDirection = 'unchanged';
      if (previousValve !== null) {
        if (previousTypeIndex > currentTypeIndex) { orderDirection = 'up'; }
        else if (previousTypeIndex < currentTypeIndex) { orderDirection = 'down'; }
      }

      return { valve, previousValve, orderDirection, isNew: previousValve === null, isDeleted: false };
    });

    // Merge deleted rows into valveRowDiffs sorted by displayOrder.
    // Within the same displayOrder, current rows appear before deleted rows.
    const deletedRows: ValveRowDiff[] = sortedPreviousValves
      .filter(pv => !matchedPreviousValves.has(pv))
      .map(v => ({
        valve: v,
        previousValve: null,
        orderDirection: 'unchanged' as RowOrderDirection,
        isNew: false,
        isDeleted: true
      }));
    this.valveRowDiffs = [...this.valveRowDiffs, ...deletedRows]
      .sort((a, b) => {
        const orderA: number = a.valve.displayOrder ?? 0;
        const orderB: number = b.valve.displayOrder ?? 0;
        if (orderA !== orderB) { return orderA - orderB; }
        // Same displayOrder: current rows before deleted rows
        return (a.isDeleted ? 1 : 0) - (b.isDeleted ? 1 : 0);
      });
    this.deletedValveRows = []; // merged into valveRowDiffs
  }

  /**
   * Returns note codes for the current valve row.
   * In compare mode all notes are shown per-row without mutual-note grouping.
   */
  getValveNoteCodes(row: ValveRowDiff): string[] {
    if (this.isCompareMode) { return (row.valve.commonNotes || []).map(n => n.codeId); }
    return this.excludeMutualNotes(row.valve.commonNotes || []).map(n => n.codeId);
  }

  /**
   * Returns note codes for the matching previous valve in a row diff.
   * In compare mode all notes are shown per-row without mutual-note grouping.
   */
  getPreviousValveNoteCodes(row: ValveRowDiff): string[] {
    if (!row.previousValve) { return []; }
    if (this.isCompareMode) { return (row.previousValve.commonNotes || []).map(n => n.codeId); }
    return this.excludePreviousMutualNotes(row.previousValve.commonNotes || []).map(n => n.codeId);
  }

  /** @deprecated Deleted rows are now part of `valveRowDiffs`. Kept for template backward-compat. */
  getPreviousDeletedValveNoteCodes(valve: ValveViewModel): string[] {
    return (valve.commonNotes || []).map(n => n.codeId);
  }

  /**
   * Filters out notes that were shared (mutual) across all valves in the previous revision.
   * Mirrors `excludeMutualNotes` but operates on `previousMutualNotes`.
   */
  private excludePreviousMutualNotes(notes: CommonNoteViewModel[]): CommonNoteViewModel[] {
    const prevMutualCodes: Set<string> = new Set(this.previousMutualNotes.map(n => n.codeId));
    return notes
      .filter(n => !prevMutualCodes.has(n.codeId))
      .sort((a, b) => Number(a.codeId) - Number(b.codeId));
  }

  // (notes that exist but no valves to associate with)
  hasOrphanedValveGroupNotes(): boolean {
    // This would be true if we stored valve group notes separately
    // For now, we'll just check if there are no valves but we expect to have notes
    // In a more complete implementation, valve group notes would be stored at pipe class level
    return !this.valvesPresent && false; // Placeholder - would check actual orphaned notes storage
  }

  // Get notes to display in the group row - either mutual notes or orphaned notes
  getDisplayableGroupNotes(): CommonNoteViewModel[] {
    if (this.mutualNotes && this.mutualNotes.length > 0) {
      return this.mutualNotes;
    }
    // If no mutual notes but we have orphaned notes, return them
    // For now returning empty array, but would return orphaned notes in complete implementation
    return [];
  }

  // Drag and Drop Methods
  onValveDrop(event: CdkDragDrop<ValveViewModel[]>): void {
    if (event.previousIndex !== event.currentIndex) {

      // Move the item in the array
      moveItemInArray(this.pipeClassViewModelValves, event.previousIndex, event.currentIndex);

      // Update display order after reordering
      this.pipeClassViewModelValves.forEach((valve, index) => {
        valve.displayOrder = index;
      });

      // Emit the reordered array to parent component
      this.reorderValvesEvent.emit([...this.pipeClassViewModelValves]);
    }
  }

  // NPS Editing Methods
  startEditingNps(valve: ValveViewModel, index: number): void {
    this.editingValveIndex = index;
    this.editingNpsValue = valve.nps || '';
    this.npsValidationError = '';
  }

  /** Resets all inline editing state to prevent stale index references after list changes */
  cancelAllEditing(): void {
    this.cancelEditingNps();
    this.cancelEditingRatings();
    this.cancelEditingEnds();
    this.cancelEditingDescription();
  }

  cancelEditingNps(): void {
    this.editingValveIndex = null;
    this.editingNpsValue = '';
    this.npsValidationError = '';
  }

  validateNpsInput(): void {
    if (this.editingNpsValue && !this.npsValidationService.isValidNps(this.editingNpsValue)) {
      this.npsValidationError = this.npsValidationService.getValidationErrorMessage();
    } else {
      this.npsValidationError = '';
    }
  }

  onNpsInput(event: any): void {
    // Allow all characters except letters (but allow 'x' for multiplication)
    const value = event.target.value;

    // Filter out letters (except 'x')
    const filteredValue = value.replace(/[a-wyzA-WYZ]/g, '');

    // Update the value if it was filtered
    if (filteredValue !== value) {
      this.editingNpsValue = filteredValue;
      event.target.value = filteredValue;
    } else {
      this.editingNpsValue = value;
    }

    // Validate the input
    this.validateNpsInput();
  }

  onNpsKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true) ||
        // Allow: home, end, left, right, down, up
        (event.keyCode >= 35 && event.keyCode <= 40)) {
      return;
    }

    // Get the character being typed
    const char = String.fromCharCode(event.keyCode);

    // Block letters except 'x' (case insensitive)
    if (/[a-wyzA-WYZ]/.test(char)) {
      event.preventDefault();
      return;
    }
  }

  /** Finds the ComponentsInPipeClassViewModel.id for a valve.
   *  Uses valve.componentInPipeClassId directly when available (the BE-assigned unique key).
   *  Falls back to componentClassificationId matching for newly added valves. */
  private getComponentsInPipeClassId(valve: ValveViewModel): string {
    // Primary: use the direct reference stored on the valve
    if (valve.componentInPipeClassId) {
      return valve.componentInPipeClassId;
    }

    // Fallback for newly added valves that don't yet have a componentInPipeClassId:
    // match by componentClassificationId within the components array
    const valveComponent = valve.components?.[0];
    if (!valveComponent?.componentId || !this.pipeClassViewModelComponents) {
      return '';
    }

    if (valveComponent.componentClassificationId) {
      const match: ComponentsInPipeClassViewModel | undefined = this.pipeClassViewModelComponents.find(
        c => c.component?.componentId === valveComponent.componentId
          && c.component?.componentClassificationId === valveComponent.componentClassificationId
      );
      if (match?.id) {
        return match.id;
      }
    }

    // Last resort: match by componentId only
    const fallback: ComponentsInPipeClassViewModel | undefined = this.pipeClassViewModelComponents.find(
      c => c.component?.componentId === valveComponent.componentId
    );

    return fallback?.id || '';
  }

  saveNps(): void {
    this.validateNpsInput();

    if (this.npsValidationError) {
      return; // Don't save if there are validation errors
    }

    const valve = this.editingValveIndex !== null ? this.pipeClassViewModelValves[this.editingValveIndex] : null;
    if (valve) {
      // Emit the change to parent component; do not mutate locally to avoid
      // affecting other rows that share the same object reference
      this.updateValveNpsEvent.emit({
        valve: valve,
        newNps: this.editingNpsValue,
        index: this.editingValveIndex,
        componentsInPipeClassId: this.getComponentsInPipeClassId(valve)
      });
    }

    // Exit editing mode
    this.cancelEditingNps();
  }

  isEditingNps(index: number): boolean {
    return this.editingValveIndex === index;
  }

  onRatingsKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true) ||
        // Allow: home, end, left, right, down, up
        (event.keyCode >= 35 && event.keyCode <= 40)) {
      return;
    }
  }

  onEndsKeydown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey === true) ||
        (event.keyCode === 67 && event.ctrlKey === true) ||
        (event.keyCode === 86 && event.ctrlKey === true) ||
        (event.keyCode === 88 && event.ctrlKey === true) ||
        // Allow: home, end, left, right, down, up
        (event.keyCode >= 35 && event.keyCode <= 40)) {
      return;
    }
  }



  //Editing Raitngs Methods
  startEditingRatings(valve: ValveViewModel, index: number): void {
    this.editingRatingIndex = index;
    this.editingRatingValue = valve.rating || '';
  }


  cancelEditingRatings(): void {
    this.editingRatingIndex = null;
    this.editingRatingValue = '';
  }

  isEditingRatings(index: number): boolean {
    return this.editingRatingIndex === index;
  }

  onRatingsInput(event: any): void {
    // Allow all characters except letters (but allow 'x' for multiplication)
    const value = event.target.value;
    this.editingRatingValue = value;
  }


  saveRatings(): void {
    const valve: ValveViewModel | undefined = this.editingRatingIndex !== null ? this.pipeClassViewModelValves[this.editingRatingIndex] : undefined;
    if (valve) {
      // Emit the change to parent component; do not mutate locally to avoid
      // affecting other rows that share the same object reference
      this.updateValveRatingsEvent.emit({
        valve: valve,
        newRatings: this.editingRatingValue,
        index: this.editingRatingIndex,
        componentsInPipeClassId: this.getComponentsInPipeClassId(valve)
      });
    }

       // Exit editing mode
       this.cancelEditingRatings();
  }

    //Editing Ends Methods
    startEditingEnds(valve: ValveViewModel, index: number): void {
      this.editingEndsIndex = index;
      this.editingEndsValue = valve.ends || '';
    }

    cancelEditingEnds(): void {
      this.editingEndsIndex = null;
      this.editingEndsValue = '';
    }

    isEditingEnds(index: number): boolean {
      return this.editingEndsIndex === index;
    }

    onEndsInput(event: any): void {
      // Allow all characters except letters (but allow 'x' for multiplication)
      const value = event.target.value;
      this.editingEndsValue = value;
    }

    startEditingDescription(valve: ValveViewModel, index: number): void {
      this.cancelAllEditing();
      this.editingDescriptionIndex = index;
      // Preload from the ComponentsInPipeClass override (PipeClassDictionaryOverride), not from
      // valve.description (catalog value). Otherwise the user sees the catalog description and
      // may think they're editing the override when they are not.
      this.editingDescriptionValue = this.getOverrideDescription(valve) || valve.description || '';
    }

    /**
     * Returns the per-pipe-class override description for a valve, resolved via its
     * ComponentsInPipeClassViewModel row (matched by componentInPipeClassId, falling back to
     * componentId for newly added valves that don't have one yet).
     */
    private getOverrideDescription(valve: ValveViewModel): string | undefined {
      const componentsInPipeClassId: string = this.getComponentsInPipeClassId(valve);
      const match: ComponentsInPipeClassViewModel | undefined = componentsInPipeClassId
        ? this.pipeClassViewModelComponents?.find(c => c.id === componentsInPipeClassId)
        : undefined;
      return match?.component?.description;
    }

    cancelEditingDescription(): void {
      this.editingDescriptionIndex = null;
      this.editingDescriptionValue = '';
    }

    isEditingDescription(index: number): boolean {
      return this.editingDescriptionIndex === index;
    }

    onDescriptionInput(event: Event): void {
      const inputElement: HTMLInputElement = event.target as HTMLInputElement;
      this.editingDescriptionValue = inputElement.value;
    }

    saveDescription(): void {
      const valve: ValveViewModel | undefined = this.editingDescriptionIndex !== null ? this.pipeClassViewModelValves[this.editingDescriptionIndex] : undefined;
      if (valve) {
        // Emit the change to parent component; do not mutate locally to avoid
        // affecting other rows that share the same object reference
        this.updateValveDescriptionEvent.emit({
          valve: valve,
          newDescription: this.editingDescriptionValue,
          index: this.editingDescriptionIndex,
          componentsInPipeClassId: this.getComponentsInPipeClassId(valve)
        });
      }

      // Exit editing mode
      this.cancelEditingDescription();
    }


    saveEnds(): void {
      const valve: ValveViewModel | undefined = this.editingEndsIndex !== null ? this.pipeClassViewModelValves[this.editingEndsIndex] : undefined;
      if (valve) {
        // Emit the change to parent component; do not mutate locally to avoid
        // affecting other rows that share the same object reference
        this.updateValveEndsEvent.emit({
          valve: valve,
          newEnds: this.editingEndsValue,
          index: this.editingEndsIndex,
          componentsInPipeClassId: this.getComponentsInPipeClassId(valve)
        });
      }

    // Exit editing mode
    this.cancelEditingEnds();
    }





  // Comment handling methods for auto-save
  onCommentsChanged(comments: CommentViewModel[]): void {
    this.commentsChanged.emit(comments);
  }

  onSaveCommentsRequested(): void {
    this.saveCommentsRequested.emit();
  }

  getLastRevisionSegment(valve: ValveViewModel): string {
    const isNew: boolean = (valve as any).__isNew;
    const isModified: boolean = (valve as any).__isModified;

    const valveComponentId: string | undefined = valve.components?.[0]?.componentId;
    const matchingComp: ComponentsInPipeClassViewModel | undefined = valve.componentInPipeClassId
      ? this.pipeClassViewModelComponents?.find(c => c.id === valve.componentInPipeClassId)
      : this.pipeClassViewModelComponents?.find(c => c.component?.componentId === valveComponentId);
    const lastUpdatedRevision: string = matchingComp?.lastUpdatedRevision || '';

    if (this.isRevisionInProgress) {
      // Revision is in progress: only show if this item was changed in the current revision
      const belongsToCurrentRevision: boolean = lastUpdatedRevision === this.currentRevisionName;
      if (!isNew && !isModified && !belongsToCurrentRevision) {
        return '';
      }
    }

    // Published/viewing mode: show lastUpdatedRevision if available
    const revision: string = lastUpdatedRevision;
    if (!revision || revision.trim() === '') {
      return '';
    }

    const segments: string[] = revision.split('-');
    return segments[segments.length - 1] || '';
  }
}
